import { ReadFileSystem } from '@playcanvas/splat-transform';
import { AppBase, Asset, GSplatResource, Model, Quat } from 'playcanvas';

import { Events } from './events';
import { loadGSplatDataWithTransform, validateGSplatData } from './io';
import { Mesh } from './mesh';
import { buildModelFromObj } from './obj-importer';
import { parseObj } from './obj-parser';
import { Splat } from './splat';

// handles loading gsplat assets using splat-transform
class AssetLoader {
    app: AppBase;
    events: Events;

    constructor(app: AppBase, events: Events) {
        this.app = app;
        this.events = events;
    }

    private getLocalBlob(fileSystem: ReadFileSystem, filename: string): Blob | null {
        const fsAny = fileSystem as any;
        if (!fsAny?.getLocalFile) {
            return null;
        }

        const basename = filename.replace(/\\/g, '/').split('/').pop() ?? filename;
        return fsAny.getLocalFile(filename) ?? fsAny.getLocalFile(basename) ?? null;
    }

    private normalizePath(path: string) {
        const parts = path.replace(/\\/g, '/').split('/');
        const normalized: string[] = [];

        for (const part of parts) {
            if (!part || part === '.') {
                continue;
            }

            if (part === '..') {
                normalized.pop();
                continue;
            }

            normalized.push(part);
        }

        return normalized.join('/');
    }

    private dirname(path: string) {
        const normalized = path.replace(/\\/g, '/');
        const idx = normalized.lastIndexOf('/');
        return idx === -1 ? '' : normalized.slice(0, idx);
    }

    private isExternalUri(path: string) {
        return /^(?:[a-z]+:)?\/\//i.test(path) || path.startsWith('data:') || path.startsWith('blob:');
    }

    private normalizeResourcePath(path: string) {
        if (!path || this.isExternalUri(path)) {
            return path;
        }

        const withoutHash = path.split('#')[0];
        const withoutQuery = withoutHash.split('?')[0];
        const normalized = this.normalizePath(withoutQuery);
        return normalized.replace(/^\/+/, '');
    }

    private joinRelativePath(baseDir: string, relativePath: string) {
        const normalizedRelative = this.normalizeResourcePath(relativePath);
        if (!normalizedRelative) {
            return '';
        }

        if (!baseDir) {
            return normalizedRelative;
        }

        return this.normalizePath(`${baseDir}/${normalizedRelative}`);
    }

    private async readBlob(fileSystem: ReadFileSystem, filename: string): Promise<Blob | null> {
        const localBlob = this.getLocalBlob(fileSystem, filename);
        if (localBlob) {
            return localBlob;
        }

        try {
            const source = await fileSystem.createSource(filename);
            const data = await source.read().readAll();
            source.close();
            return new Blob([data.slice().buffer]);
        } catch (error) {
            return null;
        }
    }

    private async hasGlbMagic(blob: Blob) {
        if (blob.size < 4) {
            return false;
        }

        const header = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
        return header[0] === 0x67 && header[1] === 0x6c && header[2] === 0x54 && header[3] === 0x46;
    }

    private async resolveBlob(fileSystem: ReadFileSystem, filename: string, referencingFilename?: string) {
        const baseDir = referencingFilename ? this.dirname(referencingFilename) : '';
        const normalizedFilename = this.normalizeResourcePath(filename);
        const decodedFilename = normalizedFilename ? this.normalizeResourcePath(decodeURIComponent(normalizedFilename)) : normalizedFilename;
        const candidates = new Set<string>();
        const addCandidate = (candidate: string) => {
            if (!candidate || this.isExternalUri(candidate)) {
                return;
            }

            candidates.add(candidate);
        };

        addCandidate(filename);
        addCandidate(normalizedFilename);
        addCandidate(decodedFilename);

        if (normalizedFilename) {
            addCandidate(normalizedFilename.replace(/^\.\//, ''));
            addCandidate(normalizedFilename.split('/').pop() ?? normalizedFilename);
        }

        if (decodedFilename) {
            addCandidate(decodedFilename.replace(/^\.\//, ''));
            addCandidate(decodedFilename.split('/').pop() ?? decodedFilename);
        }

        if (baseDir) {
            addCandidate(this.joinRelativePath(baseDir, filename));
            if (normalizedFilename) {
                addCandidate(this.joinRelativePath(baseDir, normalizedFilename));
            }
            if (decodedFilename) {
                addCandidate(this.joinRelativePath(baseDir, decodedFilename));
            }
        }

        for (const candidate of candidates) {
            const blob = await this.readBlob(fileSystem, candidate);
            if (blob) {
                return {
                    blob,
                    filename: candidate
                };
            }
        }

        return {
            blob: null,
            filename: null
        };
    }

    private async loadGltf(filename: string, fileSystem: ReadFileSystem): Promise<Mesh> {
        this.events.fire('progressStart', 'Loading mesh...');

        const tempUrls: string[] = [];
        const registerTempUrl = (blob: Blob) => {
            const url = URL.createObjectURL(blob);
            tempUrls.push(url);
            return url;
        };

        const revokeTempUrls = () => {
            tempUrls.forEach((url) => {
                try {
                    URL.revokeObjectURL(url);
                } catch (e) {
                    // ignore
                }
            });
        };

        try {
            const lower = filename.toLowerCase();
            const { blob: localBlob } = await this.resolveBlob(fileSystem, filename);
            const sourceResources = new Map<string, Blob>();

            let loadUrl = filename;

            if (localBlob) {
                if (lower.endsWith('.gltf') && !(await this.hasGlbMagic(localBlob))) {
                    const text = await localBlob.text();
                    const gltf = JSON.parse(text);

                    const remapUri = async (uri: string) => {
                        if (!uri || uri.startsWith('data:') || /^https?:/i.test(uri) || uri.startsWith('blob:')) {
                            return uri;
                        }

                        const normalizedResourcePath = this.normalizeResourcePath(uri);
                        const resolved = await this.resolveBlob(fileSystem, uri, filename);
                        if (resolved.blob) {
                            if (normalizedResourcePath) {
                                sourceResources.set(normalizedResourcePath, resolved.blob);
                            }
                            return registerTempUrl(resolved.blob);
                        }

                        return uri;
                    };

                    if (Array.isArray(gltf.buffers)) {
                        for (const buffer of gltf.buffers) {
                            if (typeof buffer?.uri === 'string') {
                                buffer.uri = await remapUri(buffer.uri);
                            }
                        }
                    }

                    if (Array.isArray(gltf.images)) {
                        for (const image of gltf.images) {
                            if (typeof image?.uri === 'string') {
                                image.uri = await remapUri(image.uri);
                            }
                        }
                    }

                    const patched = new Blob([JSON.stringify(gltf)], { type: 'model/gltf+json' });
                    loadUrl = registerTempUrl(patched);
                } else {
                    // .glb local file
                    loadUrl = registerTempUrl(localBlob);
                }
            }

            const asset = new Asset(filename, 'container', {
                url: loadUrl,
                filename
            });

            await new Promise<void>((resolve, reject) => {
                asset.once('load', () => resolve());
                asset.once('error', (err: string) => reject(new Error(`Failed to load GLTF container: ${err}`)));
                this.app.assets.add(asset);
                this.app.assets.load(asset);
            });

            const meshElement = new Mesh(asset);
            if (localBlob) {
                (meshElement as any)._sourceMeshFile = localBlob;
                (meshElement as any)._sourceMeshName = filename.replace(/\\/g, '/').split('/').pop() ?? filename;
            }
            if (sourceResources.size > 0) {
                (meshElement as any)._sourceMeshResources = sourceResources;
            }

            return meshElement;
        } finally {
            revokeTempUrls();
            this.events.fire('progressEnd');
        }
    }

    private async loadObj(filename: string, fileSystem: ReadFileSystem): Promise<Mesh> {
        this.events.fire('progressStart', 'Loading mesh...');

        try {
            const { blob: localBlob } = await this.resolveBlob(fileSystem, filename);

            if (localBlob) {
                const objText = await localBlob.text();
                const parsed = parseObj(objText);
                const model: Model = buildModelFromObj(this.app, parsed);

                const modelAsset = new Asset(filename, 'model', { filename });
                modelAsset.resource = model as any;
                this.app.assets.add(modelAsset);

                const meshElement = new Mesh(modelAsset);
                (meshElement as any)._sourceMeshFile = localBlob;
                (meshElement as any)._sourceMeshName = filename.replace(/\\/g, '/').split('/').pop() ?? filename;
                return meshElement;
            }

            const asset = new Asset(filename, 'model', {
                url: filename,
                filename
            });

            await new Promise<void>((resolve, reject) => {
                asset.once('load', () => resolve());
                asset.once('error', (err: string) => reject(new Error(`Failed to load OBJ model: ${err}`)));
                this.app.assets.add(asset);
                this.app.assets.load(asset);
            });

            return new Mesh(asset);
        } finally {
            this.events.fire('progressEnd');
        }
    }

    async load(filename: string, fileSystem: ReadFileSystem, animationFrame?: boolean, skipReorder?: boolean) {
        const lower = filename.toLowerCase();

        if (lower.endsWith('.gltf') || lower.endsWith('.glb')) {
            return this.loadGltf(filename, fileSystem);
        }

        if (lower.endsWith('.obj')) {
            return this.loadObj(filename, fileSystem);
        }

        if (!animationFrame) {
            this.events.fire('startSpinner');
        }

        try {
            // Skip reordering for animation frames (speed) or when explicitly requested (already ordered)
            const loaded = await loadGSplatDataWithTransform(filename, fileSystem, skipReorder || animationFrame);
            const { gsplatData } = loaded;
            validateGSplatData(gsplatData);

            const asset = new Asset(filename, 'gsplat', { url: `local-asset-${Date.now()}`, filename });
            this.app.assets.add(asset);
            asset.resource = new GSplatResource(this.app.graphicsDevice, gsplatData);

            return new Splat(asset, loaded.transform?.rotation ?? new Quat());
        } finally {
            if (!animationFrame) {
                this.events.fire('stopSpinner');
            }
        }
    }
}

export { AssetLoader };
